import './style/bolmatePlot.scss';

import {Icon} from '@iconify/react';

import classNames from 'classnames';

import merge from 'lodash/merge';

import React, {useCallback, useMemo} from 'react';

import {useTranslation} from 'react-i18next';

import ReactApexChart from 'react-apexcharts';

import {ApexOptions} from 'apexcharts';

import BolmateButton from '../button/bolmateButton';

import BolmateTooltip from '../tooltip/bolmateTooltip';

import PortalSelectController from '../portalSelect/portalSelectController';

import {BolmatePlotSeries} from '../../../util/interfaces';

import {BolmatePlotType} from '../../../util/types';

interface Props {
    series: Array<BolmatePlotSeries>;
    allItems?: Array<{id: string; name: string}>;
    activeIds?: Array<string>;
    onSelectionChange?: (ids: Array<string>) => void;
    onToggleSeries?: (id: string) => void;
    type?: BolmatePlotType;
    height?: number;
    reversed?: boolean;
    yAxisMin?: number;
    yAxisMax?: number;
    yAxisSuffix?: string;
    loading?: boolean;
    emptyIcon?: string;
    emptyTitle?: string;
    emptyMessage?: string;
    legend?: boolean;
    legendItems?: Array<{id: string; name: string; dashed?: boolean}>;
    selectorLabel?: string;
    hideSelector?: boolean;
    showTopN?: boolean;
    topNCount?: number;
    topNLabel?: string;
    onResetTopN?: () => void;
    noDataMessage?: string;
    toolbar?: boolean;
    borderless?: boolean;
    options?: ApexOptions;
    className?: string;
    children?: React.ReactNode;
}

const PLOT_COLORS = [
    '#3B82F6',
    '#10B981',
    '#8B5CF6',
    '#F59E0B',
    '#EC4899',
    '#06B6D4',
    '#F43F5E',
    '#84CC16',
    '#6366F1',
    '#14B8A6',
    '#D946EF',
    '#F97316',
    '#0EA5E9',
    '#A3E635',
    '#E11D48',
    '#FBBF24',
    '#8B5CF6',
    '#22D3EE',
    '#FB7185',
    '#4ADE80',
    '#A78BFA',
    '#FACC15',
    '#2DD4BF',
    '#F472B6'
];

export default function BolmatePlot(props: Props) {
    const {t} = useTranslation();
    const {
        series: inputSeries,
        allItems,
        activeIds,
        onSelectionChange,
        onToggleSeries,
        type = 'line',
        height = 250,
        reversed = false,
        yAxisMin,
        yAxisMax,
        yAxisSuffix = '',
        loading = false,
        emptyIcon = 'mdi:chart-line-variant',
        emptyTitle: emptyTitleProp,
        emptyMessage: emptyMessageProp,
        legend = true,
        legendItems,
        selectorLabel: selectorLabelProp,
        hideSelector = false,
        showTopN = false,
        topNCount = 5,
        topNLabel,
        onResetTopN,
        noDataMessage: noDataMessageProp,
        toolbar = true,
        borderless = false,
        className,
        children
    } = props;

    const {emptyTitle, emptyMessage, selectorLabel, noDataMessage} = useMemo(
        () => ({
            emptyTitle: emptyTitleProp ?? t('noData', 'Geen data'),
            emptyMessage:
                emptyMessageProp ??
                t('controls.plot.noDataMessage', 'Selecteer een serie of pas de filters aan om gegevens te bekijken.'),
            selectorLabel: selectorLabelProp ?? t('controls.plot.items', 'Items'),
            noDataMessage:
                noDataMessageProp ?? t('controls.plot.noDataSeriesMessage', 'Geen data beschikbaar voor deze serie.')
        }),
        [t, emptyTitleProp, emptyMessageProp, selectorLabelProp, noDataMessageProp]
    );

    const legendList = legendItems ?? allItems ?? inputSeries;

    // Detect series with no data (empty array or all null values)
    const emptyIds = useMemo(() => {
        const set = new Set<string>();
        for (const s of inputSeries) {
            if (s.data.length === 0 || s.data.every(d => d.value == null)) {
                set.add(s.id);
            }
        }
        return set;
    }, [inputSeries]);

    const selectorItems = useMemo(
        () =>
            (allItems ?? inputSeries).map(i => ({
                value: i.id,
                label: i.name,
                disabled: emptyIds.has(i.id)
            })),
        [allItems, inputSeries, emptyIds]
    );

    const hasSelector = !hideSelector && onSelectionChange && selectorItems.length > 1;

    // Determine visible series
    const visibleSeries = useMemo(() => {
        if (!activeIds) return inputSeries;
        return inputSeries.filter(s => activeIds.includes(s.id));
    }, [inputSeries, activeIds]);

    // Build color map
    const colorMap = useMemo(() => {
        const map: Record<string, string> = {};
        legendList.forEach((item, idx) => {
            map[item.id] = PLOT_COLORS[idx % PLOT_COLORS.length];
        });
        return map;
    }, [legendList]);

    // Compute all dates across visible series
    const allDates = useMemo(() => {
        const dateSet = new Set<string>();
        for (const s of visibleSeries) {
            for (const d of s.data) {
                dateSet.add(d.date);
            }
        }
        return Array.from(dateSet);
    }, [visibleSeries]);

    // Build ApexCharts series
    const apexSeries: ApexAxisChartSeries = useMemo(() => {
        return visibleSeries.map(s => ({
            name: s.name,
            data: allDates.map(date => {
                const point = s.data.find(d => d.date === date);
                return point?.value ?? null;
            })
        }));
    }, [visibleSeries, allDates]);

    const visibleColors = useMemo(
        () => visibleSeries.map(s => colorMap[s.id] || PLOT_COLORS[0]),
        [visibleSeries, colorMap]
    );

    const dashArray = useMemo(() => visibleSeries.map(s => (s.dashed ? 5 : 0)), [visibleSeries]);

    // Detect isolated points (value surrounded by nulls) and mark them for larger markers
    const discreteMarkers = useMemo(() => {
        const markers: Array<{
            seriesIndex: number;
            dataPointIndex: number;
            fillColor: string;
            strokeColor: string;
            size: number;
        }> = [];
        apexSeries.forEach((series, si) => {
            const data = series.data as Array<number | null>;
            for (let i = 0; i < data.length; i++) {
                if (data[i] == null) continue;
                const prevNull = i === 0 || data[i - 1] == null;
                const nextNull = i === data.length - 1 || data[i + 1] == null;
                if (prevNull && nextNull) {
                    markers.push({
                        seriesIndex: si,
                        dataPointIndex: i,
                        fillColor: visibleColors[si] || PLOT_COLORS[0],
                        strokeColor: '#fff',
                        size: 6
                    });
                }
            }
        });
        return markers;
    }, [apexSeries, visibleColors]);

    const handleSelectorChange = useCallback(
        (value: string | null, selected: boolean, values?: Array<string>) => {
            if (values && onSelectionChange) onSelectionChange(values);
        },
        [onSelectionChange]
    );

    const resetTopN = useCallback(() => {
        if (!onSelectionChange) return;
        const ref = allItems ?? inputSeries;
        const selectableIds = ref
            .filter(i => !emptyIds.has(i.id))
            .slice(0, topNCount)
            .map(i => i.id);
        onSelectionChange(selectableIds);
    }, [allItems, inputSeries, onSelectionChange, topNCount, emptyIds]);

    const handleToggle = useCallback(
        (id: string) => {
            if (emptyIds.has(id)) return;
            if (onToggleSeries) {
                onToggleSeries(id);
            } else if (onSelectionChange && activeIds) {
                if (activeIds.includes(id)) {
                    onSelectionChange(activeIds.filter(s => s !== id));
                } else {
                    onSelectionChange([...activeIds, id]);
                }
            }
        },
        [onToggleSeries, onSelectionChange, activeIds, emptyIds]
    );

    // Merge default options with user overrides
    const chartOptions: ApexOptions = useMemo(() => {
        const toolbarConfig: ApexOptions['chart'] = toolbar
            ? {
                  toolbar: {
                      show: true,
                      offsetY: -8,
                      tools: {
                          download:
                              '<svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3v12m0 0l-4-4m4 4l4-4"/><path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2"/></svg>',
                          zoom: false,
                          zoomin: false,
                          zoomout: false,
                          pan: false,
                          reset: false,
                          selection: false
                      },
                      export: {
                          svg: {filename: 'chart'},
                          png: {filename: 'chart'}
                      }
                  },
                  zoom: {enabled: false}
              }
            : {
                  toolbar: {show: false},
                  zoom: {enabled: false}
              };

        const defaults: ApexOptions = {
            chart: {
                type,
                height,
                ...toolbarConfig
            },
            stroke: {
                curve: 'smooth',
                width: 2.5,
                dashArray
            },
            colors: visibleColors,
            xaxis: {
                categories: allDates,
                labels: {
                    style: {fontSize: '0.625rem', colors: '#9CA3AF'},
                    rotate: -45,
                    rotateAlways: false
                },
                tickAmount: Math.min(allDates.length, 10)
            },
            yaxis: {
                reversed,
                min: yAxisMin,
                max: yAxisMax,
                labels: {
                    style: {fontSize: '0.6875rem', colors: '#9CA3AF'},
                    formatter: (val: number) => {
                        if (val == null) return '';
                        return reversed ? `#${Math.round(val)}` : `${Math.round(val)}${yAxisSuffix}`;
                    }
                }
            },
            tooltip: {
                enabled: true,
                shared: true,
                intersect: false,
                y: {
                    formatter: (val: number) => {
                        if (val == null) return '\u2014';
                        return reversed ? `#${val}` : `${val}${yAxisSuffix}`;
                    }
                }
            },
            grid: {
                borderColor: '#F3F4F6',
                strokeDashArray: 4
            },
            markers: {
                size: 3,
                strokeWidth: 2,
                strokeColors: '#fff',
                hover: {sizeOffset: 3},
                discrete: discreteMarkers
            },
            legend: {show: false},
            dataLabels: {enabled: false}
        };

        if (props.options) {
            return merge({}, defaults, props.options);
        }
        return defaults;
    }, [
        type,
        allDates,
        visibleColors,
        reversed,
        yAxisMin,
        yAxisMax,
        yAxisSuffix,
        height,
        dashArray,
        discreteMarkers,
        toolbar,
        props.options
    ]);

    const canToggle = !!(onToggleSeries || (onSelectionChange && activeIds));

    if (loading) {
        return (
            <div
                className={classNames('bolmate-plot', borderless && 'borderless', className)}
                style={{display: 'flex', alignItems: 'center', justifyContent: 'center', height}}
            >
                <Icon icon='mdi:loading' className='spin' style={{fontSize: '2rem', color: '#9CA3AF'}} />
            </div>
        );
    }

    return (
        <div className={classNames('bolmate-plot', borderless && 'borderless', className)}>
            {/* Legend + Selector */}
            {legend && legendList.length > 1 && inputSeries.length > 0 && (
                <div className='bolmate-plot-legend-bar'>
                    <div className='bolmate-plot-legend-items'>
                        {legendList.map(item => {
                            const isEmpty = emptyIds.has(item.id);
                            const isActive = isEmpty ? false : activeIds ? activeIds.includes(item.id) : true;
                            const isDashed = 'dashed' in item ? item.dashed : false;
                            const color = colorMap[item.id] || PLOT_COLORS[0];
                            const legendItem = (
                                <span
                                    key={item.id}
                                    className={classNames(
                                        'bolmate-plot-legend-item',
                                        !isActive && 'inactive',
                                        isEmpty && 'disabled'
                                    )}
                                    onClick={!isEmpty && canToggle ? () => handleToggle(item.id) : undefined}
                                    style={!isEmpty && canToggle ? {cursor: 'pointer'} : undefined}
                                >
                                    <span
                                        className='bolmate-plot-legend-dot'
                                        style={{
                                            backgroundColor: isDashed ? 'transparent' : color,
                                            ...(isDashed ? {border: `2px dashed ${color}`} : {})
                                        }}
                                    />
                                    <span className='bolmate-plot-legend-label'>{item.name}</span>
                                    {isEmpty && (
                                        <Icon icon='mdi:alert-circle-outline' className='bolmate-plot-legend-warning' />
                                    )}
                                </span>
                            );
                            return isEmpty ? (
                                <BolmateTooltip key={item.id} text={noDataMessage}>
                                    {legendItem}
                                </BolmateTooltip>
                            ) : (
                                legendItem
                            );
                        })}
                    </div>

                    <div className='bolmate-plot-controls'>
                        {showTopN && onSelectionChange && (
                            <BolmateButton
                                label={topNLabel ?? `Top ${topNCount}`}
                                onClick={onResetTopN ?? resetTopN}
                                size='small'
                                color='notSet'
                                tertiary
                                shape='rounded'
                            />
                        )}
                        {hasSelector && (
                            <PortalSelectController
                                items={selectorItems}
                                value={activeIds?.filter(id => !emptyIds.has(id))}
                                onChange={handleSelectorChange}
                                selectLabel={`{count} ${selectorLabel.toLowerCase()}`}
                                placeholder={selectorLabel}
                                size='small'
                                shape='rounded'
                                itemsPosition='bottom'
                                itemsHeight='12rem'
                                multiple
                                controls={['selectAll', 'deselectAll']}
                            />
                        )}
                        {children}
                    </div>
                </div>
            )}

            {/* Chart or empty state */}
            {visibleSeries.length === 0 ? (
                <div className='bolmate-plot-empty' style={{height}}>
                    <Icon icon={emptyIcon} className='bolmate-plot-empty-icon' />
                    <span className='bolmate-plot-empty-title'>{emptyTitle}</span>
                    <span className='bolmate-plot-empty-text'>{emptyMessage}</span>
                </div>
            ) : (
                <div className='bolmate-plot-chart'>
                    <ReactApexChart options={chartOptions} series={apexSeries} type={type} height={height} />
                </div>
            )}
        </div>
    );
}
